import { useRef, useState } from "react";
import axios from "axios";
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  Send,
  Mail,
  Phone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { SITE } from "@/config/site";
import { LegalModal } from "@/components/landing/Legal";
import { trackEvent, EVENTS } from "@/utils/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const INITIAL = {
  nombre: "",
  clinica: "",
  email: "",
  telefono: "",
  citas_mes: "",
  consentimiento: false,
};

const FIELDS = [
  { id: "nombre", label: "Nombre y apellidos", type: "text", required: true, autoComplete: "name" },
  { id: "clinica", label: "Nombre de la clínica", type: "text", required: true, autoComplete: "organization" },
  { id: "email", label: "Email profesional", type: "email", required: true, autoComplete: "email" },
  { id: "telefono", label: "Teléfono", type: "tel", required: true, autoComplete: "tel" },
  { id: "citas_mes", label: "Número aproximado de citas al mes", type: "number", required: true, min: 1 },
];

export default function AuditForm() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const started = useRef(false);

  const onFocus = () => {
    if (!started.current) {
      started.current = true;
      trackEvent(EVENTS.FORM_START);
    }
  };

  const set = (id, value) => {
    setForm((f) => ({ ...f, [id]: value }));
    setErrors((e) => ({ ...e, [id]: undefined }));
  };

  const validate = () => {
    const errs = {};
    for (const f of FIELDS) {
      const v = String(form[f.id]).trim();
      if (f.required && !v) {
        errs[f.id] = "Campo obligatorio";
      } else if (f.type === "email" && v && !EMAIL_RE.test(v)) {
        errs[f.id] = "Introduce un email válido";
      } else if (f.type === "number" && v) {
        const n = Number(v);
        if (Number.isNaN(n) || (f.min !== undefined && n < f.min) || (f.max !== undefined && n > f.max)) {
          errs[f.id] = `Valor entre ${f.min ?? 0} y ${f.max ?? "∞"}`;
        }
      }
    }
    if (form.telefono && form.telefono.replace(/\D/g, "").length < 6) {
      errs.telefono = "Introduce un teléfono válido";
    }
    if (!form.consentimiento) {
      errs.consentimiento = "Debes aceptar la política de privacidad";
    }
    return errs;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setStatus("idle");
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/audit-requests`, {
        nombre: form.nombre.trim(),
        clinica: form.clinica.trim(),
        email: form.email.trim(),
        telefono: form.telefono.trim(),
        citas_mes: Number(form.citas_mes),
        consentimiento: form.consentimiento,
      });
      setStatus("success");
      trackEvent(EVENTS.FORM_SUBMIT);
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <section id="auditoria" className="bg-platinum py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-start gap-12 lg:grid-cols-5 lg:gap-16">
          <div className="lg:col-span-2">
            <p className="text-sm font-semibold uppercase tracking-widest text-navy-light">
              Auditoría gratuita
            </p>
            <h2
              data-testid="audit-title"
              className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:leading-[1.15]"
            >
              Descubre cuánto dinero puedes estar perdiendo por los no-shows.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-steel sm:text-lg">
              Cuéntanos cómo trabaja tu clínica y analizaremos tu oportunidad de
              recuperación: huecos perdidos, citas en riesgo y cuánto podrías recuperar
              con confirmación inteligente.
            </p>
            <ul className="mt-7 space-y-3 text-sm font-medium text-navy">
              {[
                "Análisis personalizado de tu volumen de no-shows.",
                "Estimación de ingresos recuperables según tu caso.",
                "Propuesta de conexión con tu software actual.",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-recover" strokeWidth={2} />
                  {item}
                </li>
              ))}
            </ul>
            <div className="mt-8 space-y-2 text-sm text-steel">
              <p className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-navy-light" />
                <a
                  href={`mailto:${SITE.email}`}
                  data-testid="audit-email-link"
                  className="font-medium text-navy hover:underline"
                >
                  {SITE.email}
                </a>
              </p>
              <p className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-navy-light" />
                <a
                  href={SITE.phoneHref}
                  data-testid="audit-phone-link"
                  className="font-medium text-navy hover:underline"
                >
                  {SITE.phoneDisplay}
                </a>
              </p>
            </div>
          </div>

          <div className="lg:col-span-3">
            {status === "success" ? (
              <div
                data-testid="audit-success"
                className="flex min-h-[420px] flex-col items-center justify-center rounded-2xl border border-[#DDEAE2] bg-white p-10 text-center shadow-[0_24px_60px_-24px_rgba(11,31,58,0.18)]"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#E7F4EC]">
                  <CheckCircle2 className="h-8 w-8 text-recover" strokeWidth={1.8} />
                </span>
                <h3 className="mt-6 font-heading text-2xl font-bold text-navy">
                  Solicitud recibida.
                </h3>
                <p className="mt-3 max-w-md text-base leading-relaxed text-steel">
                  Analizaremos la información de tu clínica y nos pondremos en contacto
                  contigo para revisar tu oportunidad de recuperación.
                </p>
              </div>
            ) : (
              <form
                data-testid="audit-form"
                onSubmit={onSubmit}
                noValidate
                className="rounded-2xl border border-[#E4E9F2] bg-white p-6 shadow-[0_24px_60px_-24px_rgba(11,31,58,0.18)] sm:p-8"
              >
                <div className="grid gap-5 sm:grid-cols-2">
                  {FIELDS.map((f) => (
                    <div key={f.id}>
                      <Label htmlFor={`field-${f.id}`} className="text-sm font-semibold text-navy">
                        {f.label}
                        {f.required && <span className="text-navy-light"> *</span>}
                      </Label>
                      <div className="relative mt-1.5">
                        <Input
                          id={`field-${f.id}`}
                          data-testid={`audit-input-${f.id}`}
                          type={f.type}
                          min={f.min}
                          max={f.max}
                          autoComplete={f.autoComplete}
                          value={form[f.id]}
                          onFocus={onFocus}
                          onChange={(e) => set(f.id, e.target.value)}
                          aria-invalid={!!errors[f.id]}
                          className={`h-11 rounded-lg bg-white text-navy focus-visible:ring-navy-light ${
                            errors[f.id] ? "border-red-700" : "border-[#DCE5F0]"
                          } ${f.suffix ? "pr-10" : ""}`}
                        />
                        {f.suffix && (
                          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-steel">
                            {f.suffix}
                          </span>
                        )}
                      </div>
                      {errors[f.id] && (
                        <p data-testid={`audit-error-${f.id}`} className="mt-1.5 flex items-center gap-1 text-xs font-medium text-red-700">
                          <AlertCircle className="h-3 w-3" />
                          {errors[f.id]}
                        </p>
                      )}
                    </div>
                  ))}

                  <div className="sm:col-span-2">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="field-consentimiento"
                        data-testid="audit-checkbox-consentimiento"
                        checked={form.consentimiento}
                        onCheckedChange={(checked) => set("consentimiento", checked === true)}
                        aria-invalid={!!errors.consentimiento}
                        className="mt-0.5 border-[#B9C6D8] data-[state=checked]:bg-navy data-[state=checked]:text-white"
                      />
                      <Label
                        htmlFor="field-consentimiento"
                        className="cursor-pointer text-sm font-normal leading-relaxed text-steel"
                      >
                        Acepto la{" "}
                        <LegalModal
                          type="privacidad"
                          testId="audit-privacy-link"
                          trigger={
                            <button
                              type="button"
                              className="font-semibold text-navy underline decoration-navy/30 underline-offset-2 hover:decoration-navy"
                            >
                              política de privacidad
                            </button>
                          }
                        />{" "}
                        y autorizo el contacto para recibir información sobre la auditoría
                        solicitada.
                      </Label>
                    </div>
                    {errors.consentimiento && (
                      <p data-testid="audit-error-consentimiento" className="mt-1.5 flex items-center gap-1 text-xs font-medium text-red-700">
                        <AlertCircle className="h-3 w-3" />
                        {errors.consentimiento}
                      </p>
                    )}
                  </div>
                </div>

                {status === "error" && (
                  <div
                    data-testid="audit-error-general"
                    role="alert"
                    className="mt-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800"
                  >
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    No hemos podido enviar tu solicitud. Revisa tu conexión e inténtalo de
                    nuevo, o escríbenos directamente a {SITE.email}.
                  </div>
                )}

                <Button
                  type="submit"
                  data-testid="audit-submit-button"
                  disabled={status === "loading"}
                  className="mt-7 w-full rounded-lg bg-navy py-6 text-base font-semibold text-white transition-all duration-200 hover:bg-navy-light disabled:opacity-70"
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Enviando solicitud…
                    </>
                  ) : (
                    <>
                      Solicitar mi auditoría gratuita
                      <Send className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>

                <p className="mt-4 text-center text-xs text-steel">
                  Sin compromiso. Tus datos se usan únicamente para preparar y gestionar tu
                  auditoría.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
